import { ChangeEvent, Dispatch, SetStateAction, useCallback, useContext, useState } from 'react';
import {
	H3,
	Text,
	Spacer,
	InputField,
} from '@components';
import { EditShipment } from '@state';
import { useRecoilState } from 'recoil';
import { TShipmentOrder, TEditShipmentErrors } from '@types';
import { EditShipmentContext } from '../EditShipment.Context';

type TProps = {
	errors?: TEditShipmentErrors | null;
	setErrors?: Dispatch<SetStateAction<TEditShipmentErrors | null>>;
}

const PO_MAX_LENGTH = 120;

export const ReferenceNumbers = ({ errors, setErrors }: TProps) => {
	const ctx = useContext(EditShipmentContext);
	const shipperRefDisabled = ctx?.access.pages.referenceNumbers.fields.shipperRef.disabled ?? false;
	const [shipment, setShipment] = useRecoilState(EditShipment);
	const [poError, setPoError] = useState<string | undefined>(undefined);

	const orderNumbers = useCallback(() => {
		const orderNumbers: string[] = [];
		if (shipment) {
			if (shipment.shipper_ref1 && shipment.shipper_ref1 !== '') orderNumbers.push(shipment.shipper_ref1);
			if (shipment.shipper_ref2 && shipment.shipper_ref2 !== '') orderNumbers.push(shipment.shipper_ref2);
			if (shipment.shipper_ref3 && shipment.shipper_ref3 !== '') orderNumbers.push(shipment.shipper_ref3);
		}
		return orderNumbers.join(', ');
	}, [shipment]);

	const updateOrderNumbers = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const update = {
			...shipment,
			shipper_ref1: value,
			shipper_ref2: '',
			shipper_ref3: ''
		} as TShipmentOrder;
		setShipment(update);
		if (ctx?.setTouched) ctx.setTouched(true);
	};

	const updatePoReference = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		if (value.length > PO_MAX_LENGTH) {
			setPoError(`Must be ${PO_MAX_LENGTH} characters or less`);
		} else {
			setPoError(undefined);
		}
		const update = {
			...shipment,
			po_reference: value,
		} as TShipmentOrder;
		setShipment(update);
		if (ctx?.setTouched) ctx.setTouched(true);
		if (errors?.fields?.['po_reference'] && setErrors) {
			setErrors((prev) => prev ? { ...prev, fields: { ...prev.fields, po_reference: [] } } : prev);
		}
	};

	const serverPoError = errors?.fields?.['po_reference']?.[0];

	return (
		<>
			<H3>Update reference numbers</H3>
			<Spacer size={16} />
			<Text>Update reference information to ensure accurate shipment records.</Text>
			<Spacer size={16} />
			<InputField
				name={''}
				label={'Order #'}
				value={orderNumbers()}
				onChange={updateOrderNumbers}
				disabled={shipperRefDisabled}
			/>
			<Spacer size={16} />
			<InputField
				name={''}
				label={'Purchase Order #'}
				value={shipment?.po_reference ?? ''}
				onChange={updatePoReference}
				hasError={!!(poError ?? serverPoError)}
				errorMessage={poError ?? serverPoError}
			/>
		</>
	);
}
